import { useCallback } from 'react';
import { useFetcher } from 'react-router';

import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import type {
  Payload,
  UserExistsResponse,
} from '@jane/bloom/routes/api.user-exists/types';
import { buildFormData } from '@jane/bloom/utils/formData';

interface CheckUserExistsOptions {
  /**
   * When true, the action skips its default redirect to `/login/email` or
   * `/signup` and returns `{ isNewUser }` JSON. Callers driving their own UI
   * (e.g. the in-page auth modal) should pass this.
   */
  skipRedirect?: boolean;
}

export const useCheckJaneUserExists = ({
  skipRedirect,
}: CheckUserExistsOptions = {}) => {
  const basename = useBloomBasename();
  const fetcher = useFetcher<UserExistsResponse>();

  const checkUserExists = useCallback(
    (email: string) => {
      const formData: Payload = {
        email,
        referrer: window.location.href,
        skipRedirect: skipRedirect ? 'true' : 'false',
      };
      return fetcher.submit(buildFormData(formData), {
        action: `${basename}api/user-exists`,
        method: 'post',
        preventScrollReset: true,
      });
    },
    [basename, fetcher, skipRedirect]
  );

  return { checkUserExists, fetcher };
};
